import { create } from 'zustand'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { DEFAULT_DISPLAY_TIME_ZONE, resolveTimeZone } from 'lib/helpers/resolveTimeZone/resolveTimeZone'
import { isSSR } from 'lib/helpers/isSSR/isSSR'

export const FALLBACK_TIME_ZONE = DEFAULT_DISPLAY_TIME_ZONE

interface ActiveTimeZoneState {
  overriddenTimeZone: Maybe<string>
  deviceTimeZone: Maybe<string>
  hasProfilePreference: boolean
}

// Both zones start empty so the server snapshot matches the first client render: never seed them here.
const useActiveTimeZoneStore = create<ActiveTimeZoneState>(() => ({
  overriddenTimeZone: null,
  deviceTimeZone: null,
  hasProfilePreference: false,
}))

const resolveActiveTimeZone = ({
  overriddenTimeZone,
  deviceTimeZone,
  hasProfilePreference,
}: ActiveTimeZoneState): string =>
  overriddenTimeZone ?? (hasProfilePreference ? deviceTimeZone : null) ?? FALLBACK_TIME_ZONE

export const getActiveTimeZone = (): string => {
  if (isSSR()) {
    return FALLBACK_TIME_ZONE
  }

  const { overriddenTimeZone, deviceTimeZone } = useActiveTimeZoneStore.getState()

  return overriddenTimeZone ?? deviceTimeZone ?? resolveTimeZone(getDeviceTimeZone()) ?? FALLBACK_TIME_ZONE
}

export const useActiveTimeZone = (): Maybe<string> => {
  const { timezoneAwareTimestamps = false } = useFlags()
  const activeTimeZone = useActiveTimeZoneStore(resolveActiveTimeZone)

  return timezoneAwareTimestamps ? activeTimeZone : null
}

export const useDeviceTimeZone = (): Maybe<string> => useActiveTimeZoneStore(({ deviceTimeZone }) => deviceTimeZone)

interface SetActiveTimeZoneOptions {
  hasProfilePreference?: boolean
}

export const setActiveTimeZone = (
  overriddenTimeZone: Maybe<string>,
  { hasProfilePreference }: SetActiveTimeZoneOptions = {},
): void => {
  if (isSSR()) {
    return
  }

  const resolvedTimeZone = resolveTimeZone(overriddenTimeZone)

  useActiveTimeZoneStore.setState({
    overriddenTimeZone: resolvedTimeZone,
    hasProfilePreference: hasProfilePreference ?? resolvedTimeZone !== null,
  })
}

export const setDeviceTimeZone = (deviceTimeZone: Maybe<string>): void => {
  if (isSSR()) {
    return
  }

  useActiveTimeZoneStore.setState({ deviceTimeZone: resolveTimeZone(deviceTimeZone) })
}

export const getDeviceTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone

export const initActiveTimeZone = (): void => {
  if (isSSR()) {
    return
  }

  setDeviceTimeZone(getDeviceTimeZone())
}
